import {
  type XuwoBrandIconData,
  xuwoBrandIconData,
  type XuwoBrandIconName,
} from './brand';
import { type XuwoIconData, xuwoIconData, type XuwoIconName } from './data';

/**
 * 框架无关的图标解算核心 —— DOM 的 `xuwoIconNode` 与 React 的 `XuwoIcon` 都只调它，
 * 让「一种渲染方式」落在**一处实现**上，而不是两份长得像的代码。
 * （这两份此前确实分叉过：Lit 的 size 收 `number | string`，React 的只收
 * `number`，而两边注释都写着"同一份约定"。）
 *
 * `size` 是高度，宽度按 viewBox 宽高比计算。共享库保留各资产的
 * 原坐标系，光学校准在资产内部完成，调用方不按来源额外缩放。
 *
 * `size` 允许带单位（`'1em'`）。必须连单位一起换算——只取数值会把 `'1em'`
 * 当成 1px，图标直接缩成一条线。
 */
export const resolveXuwoIcon = (
  name: XuwoIconName,
  size: number | string
): XuwoIconData & { width: number | string; height: number | string } => {
  const data = xuwoIconData[name] ??
    xuwoIconData.page ?? {
      viewBox: '0 0 20 20',
      content: '',
    };
  const [, , vbWidth, vbHeight] = data.viewBox.trim().split(/\s+/).map(Number);
  const ratio = vbWidth && vbHeight ? vbWidth / vbHeight : 1;
  const parsed =
    typeof size === 'number'
      ? { value: size, unit: '' }
      : (([, value, unit]) => ({ value: Number(value), unit }))(
          /^([\d.]+)(.*)$/.exec(size.trim()) ?? ['', '', '']
        );
  const width = Number.isFinite(parsed.value)
    ? `${Math.round(parsed.value * ratio * 1000) / 1000}${parsed.unit}`
    : size;
  return { ...data, width, height: size };
};

export type XuwoBrandIconVariant = 'mono' | 'color';

/**
 * 品牌图标解算 —— 与 `resolveXuwoIcon` 同一套 size 约定（`size` 是高度），
 * 只是资产族不同：见 `brand.ts` 的文件头。
 *
 * `variant: 'color'` 在没有彩色版的品牌上**自动落回单色**，而不是抛错或留空——
 * 这批 slug 是按模型名推断出来的，调用点不该为「这家有没有彩色版」写分支。
 *
 * `uid` 是**实例唯一串**（React 传 `useId()`）。彩色版里的 `<linearGradient>`
 * id 会跨实例撞车：同一页面渲染两个 gemini 时，`url(#...)` 只解析到文档里
 * 第一个同名节点，第一个实例卸载后第二个就会失去渐变。资产里统一留了
 * `__UID__` 占位符，这里替换掉。单色版不含 id，`uid` 可省。
 */
export const resolveXuwoBrandIcon = (
  name: XuwoBrandIconName,
  {
    variant = 'mono',
    size = '1em',
    uid = '',
  }: {
    variant?: XuwoBrandIconVariant;
    size?: number | string;
    uid?: string;
  } = {}
): {
  viewBox: string;
  content: string;
  fillRule?: XuwoBrandIconData['fillRule'];
  width: number | string;
  height: number | string;
  /** 彩色版自带 fill，root 不能再压一个 currentColor 上去 */
  fill: 'currentColor' | 'none';
} => {
  const data = xuwoBrandIconData[name];
  const useColor = variant === 'color' && data.color !== undefined;
  const raw = useColor ? (data.color as string) : data.mono;
  const content =
    useColor && data.colorHasLocalIds
      ? raw.split('__UID__').join(uid.replace(/[^\w-]/g, '') || 'x')
      : raw;
  // 品牌资产全部是 24 满框正方形，宽高比恒为 1；仍走同一套换算以免将来放宽。
  const [, , vbWidth, vbHeight] = data.viewBox.trim().split(/\s+/).map(Number);
  const ratio = vbWidth && vbHeight ? vbWidth / vbHeight : 1;
  const parsed =
    typeof size === 'number'
      ? { value: size, unit: '' }
      : (([, value, unit]) => ({ value: Number(value), unit }))(
          /^([\d.]+)(.*)$/.exec(size.trim()) ?? ['', '', '']
        );
  const width = Number.isFinite(parsed.value)
    ? `${Math.round(parsed.value * ratio * 1000) / 1000}${parsed.unit}`
    : size;
  return {
    viewBox: data.viewBox,
    content,
    fillRule: useColor ? undefined : data.fillRule,
    width,
    height: size,
    fill: useColor ? 'none' : 'currentColor',
  };
};
